/**
 * Whether to show the "most people are nervous about this" acknowledgement (spec A6b).
 *
 * Loora runs the equivalent screen and ScreensDesign scores it the single highest unique
 * moment in its onboarding — "rare in onboarding flows". None of the other nineteen apps
 * studied addresses the fear at all. For a speaking product the fear of sounding stupid
 * is the actual barrier, and we already hold the two mechanisms that answer it: a score
 * we are willing to withhold, and text mode as a first-class path rather than a
 * degraded one. This screen exists to say so.
 *
 * The decision lives here, not in the three onboarding views, for the same reason the
 * paywall decision does: "never twice" and "never to someone who just did well" are
 * properties of the system, not conventions three client teams have to remember.
 */

import type { ScoreStatus } from '../types.js';

/** Why we think this learner is nervous. Reported with the analytics event. */
export type AnxietySignal = 'short_first_utterance' | 'retry_before_submit' | 'judged_motivation';

export type AnxietyAckBlockReason = 'already_seen' | 'spoke_well' | 'no_signal';

/** A motivation that implies being judged by someone. */
export type JudgedMotivation = 'work' | 'exam' | 'moving';

const JUDGED: readonly string[] = ['work', 'exam', 'moving'];

/**
 * Under this, an utterance is too short to be an attempt at the prompt — it is someone
 * stopping as soon as they hear themselves. Two seconds is roughly a word and a half at
 * a nervous speaking rate; our own prompts cannot be answered in that time.
 */
export const SHORT_UTTERANCE_MS = 2000;

export interface AnxietyAckInput {
  /** Shown once, ever. Persisted per learner, not per install. */
  readonly alreadySeen: boolean;
  /** Length of their first recorded utterance, or null if they have not spoken yet. */
  readonly firstUtteranceMs: number | null;
  /** Times they discarded a recording and started again before submitting one. */
  readonly retriesBeforeSubmit: number;
  /** Their A7 motivations. */
  readonly motivations: readonly string[];
  /** How their first utterance was scored. */
  readonly firstUtteranceStatus: ScoreStatus;
  /** 0–100, present only when `firstUtteranceStatus` is `scored`. */
  readonly firstUtteranceScore: number | null;
}

export interface AnxietyAckDecision {
  readonly show: boolean;
  /** The strongest signal, for `onboarding_anxiety_ack_view {signal}`. */
  readonly signal: AnxietySignal | null;
  /** Every signal present, strongest first. */
  readonly signals: readonly AnxietySignal[];
  readonly blockedBy: AnxietyAckBlockReason | null;
}

/**
 * A first utterance at or above this is a learner who spoke and was understood. Telling
 * them other people find this frightening reads as condescension, so we don't.
 */
export const SPOKE_WELL_SCORE = 80;

/**
 * Ordered by how directly each one evidences the fear. A clipped recording is the
 * learner flinching in the moment; a motivation is only a prior.
 */
function detect(input: AnxietyAckInput): readonly AnxietySignal[] {
  const signals: AnxietySignal[] = [];
  if (input.firstUtteranceMs !== null && input.firstUtteranceMs < SHORT_UTTERANCE_MS) {
    signals.push('short_first_utterance');
  }
  if (input.retriesBeforeSubmit > 0) signals.push('retry_before_submit');
  if (input.motivations.some((m) => JUDGED.includes(m))) signals.push('judged_motivation');
  return signals;
}

export function shouldShowAnxietyAck(input: AnxietyAckInput): AnxietyAckDecision {
  const signals = detect(input);
  const deny = (blockedBy: AnxietyAckBlockReason): AnxietyAckDecision => ({
    show: false,
    signal: null,
    signals,
    blockedBy,
  });

  if (input.alreadySeen) return deny('already_seen');

  // Only a real score suppresses it. A withheld score is precisely the case this screen
  // explains, so suppressing on it would hide the explanation exactly when it is owed —
  // and `unavailable` means we never heard them, which says nothing about how they did.
  if (
    input.firstUtteranceStatus === 'scored' &&
    input.firstUtteranceScore !== null &&
    input.firstUtteranceScore >= SPOKE_WELL_SCORE
  ) {
    return deny('spoke_well');
  }

  const strongest = signals[0];
  if (strongest === undefined) return deny('no_signal');

  return { show: true, signal: strongest, signals, blockedBy: null };
}

// ── Copy ──────────────────────────────────────────────────────────────────────

/**
 * The body is per-signal because a generic reassurance is the version that reads as
 * filler. Each one names what we actually observed, then states the two mechanisms.
 */
export interface AnxietyAckCopy {
  readonly title: string;
  readonly body: string;
  readonly reassurances: readonly [string, string];
  readonly primaryCta: string;
  readonly secondaryCta: string;
}

export function anxietyAckCopy(signal: AnxietySignal): AnxietyAckCopy {
  const bodies: Record<AnxietySignal, string> = {
    short_first_utterance:
      'You stopped early back there. Almost everyone does on their first go — ' +
      'hearing your own voice in another language is genuinely unpleasant at first.',
    retry_before_submit:
      'You had a few goes at that. Most people do. Nobody sounds the way they want ' +
      'to in a language they are still learning.',
    judged_motivation:
      'Speaking in front of someone who might be judging you is the hard part — ' +
      'harder than the grammar, and most apps pretend otherwise.',
  };

  return {
    title: 'Most people are nervous about this',
    body: bodies[signal],
    reassurances: [
      // Both of these are real mechanisms, not promises: ScoreStatus.withheld exists in
      // the domain, the DB, the contract and every client, and text mode is a full path.
      'We never show you a score we are not sure of. If the audio is poor or the sound ' +
        'is one we cannot judge fairly, we say so instead of guessing.',
      'You can switch to typing whenever you like and switch back. Nothing is lost and ' +
        'nothing is locked behind speaking.',
    ],
    primaryCta: "I'll speak",
    secondaryCta: 'Start in text instead',
  };
}
